import apiService from 'app/apiService';
import type { APIResponseType } from 'app/apiService/types';

export interface AutomationNeedingAttentionResolveRequest {
  dealer_id: string;
  segment_name: string;
  reason_code: string;
  opportunity_type?: 'test' | 'active';
  schedule_at?: string;
  use_cadence?: boolean;
}

export interface AutomationNeedingAttentionRerunWebsocket {
  subscribe_job_topic?: string;
  subscribe_dealer_topic?: string;
  events?: string[];
}

export interface AutomationNeedingAttentionRerun {
  test_automation_id?: string;
  task_ids?: string[];
  opportunity_ids?: string[];
  items?: Array<{
    task_id?: string;
    opportunity_id?: string;
    lead_id?: string;
    record_type?: string;
  }>;
  websocket?: AutomationNeedingAttentionRerunWebsocket;
}

export interface AutomationNeedingAttentionSegmentResult {
  segment_name: string;
  reason_code: string;
  success: boolean;
  error?: string;
  rescheduled_count?: number;
  task_ids?: string[];
  failures?: Array<{ task_id?: string; error?: string }>;
}

export interface AutomationNeedingAttentionResolveBody {
  segment_name?: string;
  reason_code?: string;
  rescheduled_count?: number;
  task_ids?: string[];
  failures?: Array<{ task_id?: string; error?: string; segment_name?: string; reason_code?: string }>;
  reruns?: AutomationNeedingAttentionRerun[];
  websocket?: AutomationNeedingAttentionRerunWebsocket;
  total_segments?: number;
  succeeded_segments?: number;
  failed_segments?: number;
  segment_results?: AutomationNeedingAttentionSegmentResult[];
}

export interface AutomationNeedingAttentionRetryAllRequest {
  dealer_id: string;
  opportunity_type?: 'test' | 'active';
  /** Defaults to `functional` on the API when omitted. */
  error_category?: 'functional' | 'system';
  /** UI hint for WebSocket progress total while segments are resolved server-side. */
  total_segments_hint?: number;
}

export interface AutomationNeedingAttentionRetryAllAcceptedBody {
  job_id?: string;
  dealer_id?: string;
  status?: string;
  total_segments_hint?: number;
  websocket?: AutomationNeedingAttentionRerunWebsocket;
}

class HumanInTheLoopApiService {
  private static readonly RESOLVE_ENDPOINT =
    'api/v2/nurturing-automation/automation-needing-attention/resolve';

  private static readonly RETRY_ALL_ENDPOINT =
    'api/v2/nurturing-automation/automation-needing-attention/retry-all';

  /**
   * `POST api/v2/nurturing-automation/automation-needing-attention/resolve?dealer_id=…`
   * Body: `dealer_id`, `segment_name`, `reason_code`, optional `opportunity_type`, etc.
   */
  static async postAutomationNeedingAttentionResolve(
    payload: AutomationNeedingAttentionResolveRequest,
  ): Promise<APIResponseType<AutomationNeedingAttentionResolveBody>> {
    const dealerId = payload.dealer_id?.trim();
    const segmentName = payload.segment_name?.trim();
    const reasonCode = payload.reason_code?.trim();
    if (!dealerId) {
      throw new Error('Dealer ID is required');
    }
    if (!segmentName) {
      throw new Error('Segment name is required');
    }
    if (!reasonCode) {
      throw new Error('Reason code is required');
    }

    const body: AutomationNeedingAttentionResolveRequest = {
      dealer_id: dealerId,
      segment_name: segmentName,
      reason_code: reasonCode,
    };
    if (payload.opportunity_type) {
      body.opportunity_type = payload.opportunity_type;
    }
    if (payload.schedule_at) {
      body.schedule_at = payload.schedule_at;
    }
    if (payload.use_cadence != null) {
      body.use_cadence = payload.use_cadence;
    }

    return apiService.post<AutomationNeedingAttentionResolveBody>(
      HumanInTheLoopApiService.RESOLVE_ENDPOINT,
      body,
      { params: { dealer_id: dealerId } },
      false,
    );
  }

  /**
   * `POST api/v2/nurturing-automation/automation-needing-attention/retry-all?dealer_id=…`
   * Retries all functional segments needing attention for the dealer + opportunity type.
   */
  static async postAutomationNeedingAttentionRetryAll(
    payload: AutomationNeedingAttentionRetryAllRequest,
  ): Promise<
    APIResponseType<
      AutomationNeedingAttentionResolveBody | AutomationNeedingAttentionRetryAllAcceptedBody
    >
  > {
    const dealerId = payload.dealer_id?.trim();
    if (!dealerId) {
      throw new Error('Dealer ID is required');
    }

    const body: AutomationNeedingAttentionRetryAllRequest = {
      dealer_id: dealerId,
      opportunity_type: payload.opportunity_type,
      error_category: payload.error_category ?? 'functional',
    };
    if (
      typeof payload.total_segments_hint === 'number' &&
      Number.isFinite(payload.total_segments_hint) &&
      payload.total_segments_hint > 0
    ) {
      body.total_segments_hint = payload.total_segments_hint;
    }

    return apiService.post<
      AutomationNeedingAttentionResolveBody | AutomationNeedingAttentionRetryAllAcceptedBody
    >(
      HumanInTheLoopApiService.RETRY_ALL_ENDPOINT,
      body,
      { params: { dealer_id: dealerId } },
      false,
    );
  }
}

export default HumanInTheLoopApiService;
